<template>
  <b-card class="translation-editor">
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <feather-icon :icon="icon" size="16" class="mr-1" />
          {{ title }}
        </h6>
        <div class="d-flex align-items-center ml-1">
          <b-badge :variant="translateMissingOnly ? 'warning' : 'primary'" class="ml-2">
            {{ translationsBadgeText }}
          </b-badge>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <div class="d-flex align-items-center mr-1">
          <b-form-checkbox v-model="translateMissingOnly" class="ai-mode-checkbox" />
          <span class="text-muted">Przetłumacz tylko brakujące</span>
        </div>

        <div class="d-flex align-items-center mr-1">
          <b-form-checkbox v-model="showMissingOnly" />
          <span class="text-muted">Pokaż nieprzetłumaczone</span>
        </div>

        <b-button
          variant="outline-primary"
          size="sm"
          class="mr-2"
          @click="handleTranslateAllWithAI"
          :disabled="Object.keys(translations).length === 0 || isTranslating">
          <b-spinner v-if="isTranslating" small class="mr-1"></b-spinner>
          <feather-icon v-else icon="ZapIcon" size="14" class="mr-1" />
          {{ isTranslating ? translationProgress.message || 'Tłumaczenie...' : 'Tłumacz z AI' }}
        </b-button>
      </div>
    </template>

    <!-- Search -->
    <div class="mb-3">
      <b-input-group>
        <template #prepend v-if="selectedTranslationKey">
          <b-input-group-text class="bg-primary text-white">
            <feather-icon icon="SearchIcon" size="14" class="mr-1" />
            <small>Wybrano z wyszukiwania globalnego</small>
          </b-input-group-text>
        </template>
        <b-form-input v-model="searchTerm" placeholder="Szukaj w tłumaczeniach..." @input="handleSearch" />
        <template #append v-if="searchTerm">
          <b-button variant="outline-secondary" @click="clearSearch">
            <feather-icon icon="XIcon" size="14" />
          </b-button>
        </template>
      </b-input-group>
    </div>

    <!-- Translations List -->
    <div class="translations-list">
      <div v-for="(value, key) in filteredTranslations" :key="key" class="translation-item mb-1 p-1 border rounded">
        <!-- Translation Key Header -->
        <div class="d-flex justify-content-between align-items-start mb-1">
          <div class="translation-key">
            <strong class="text-primary">{{ key }}</strong>
          </div>
          <b-button-group size="sm">
            <b-button variant="primary" @click="editTranslation(key)">
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
          </b-button-group>
        </div>

        <!-- Multi-language inputs -->
        <template v-if="editingKey === key">
          <div class="multi-language-editor">
            <h6 class="mb-1 text-muted">Edycja tłumaczenia dla wszystkich języków:</h6>

            <div class="row">
              <div v-for="language in availableLanguages" :key="language.lang" class="col-md-6 mb-1">
                <b-form-group
                  :label="`${language.value} (${language.lang.toUpperCase()})`"
                  :label-for="`input-${key}-${language.lang}`">
                  <template v-if="!isLocaleAvailable(language.lang)">
                    <div class="d-flex align-items-center text-muted">
                      <feather-icon icon="AlertCircleIcon" size="14" class="mr-2"></feather-icon>
                      <span>Lokalizacja niedostępna</span>
                    </div>
                  </template>
                  <template v-else-if="isLocaleLoading(language.lang)">
                    <div class="d-flex align-items-center">
                      <b-spinner small class="mr-2"></b-spinner>
                      <span class="text-muted">Ładowanie...</span>
                    </div>
                  </template>
                  <template v-else>
                    <validation-provider
                      #default="{ errors }"
                      :name="`Wartość tłumaczenia ${language.value}`"
                      :rules="getValidationRules()">
                      <b-form-textarea
                        :id="`input-${key}-${language.lang}`"
                        v-model="editingValues[getLocaleCode(language.lang)]"
                        :rows="getTextareaRows(editingValues[getLocaleCode(language.lang)])"
                        :placeholder="`Wprowadź tłumaczenie w języku ${language.value.toLowerCase()}`"
                        :state="errors.length === 0 ? null : false" />
                      <small v-if="errors.length" class="text-danger d-block">
                        {{ errors[0] }}
                      </small>
                    </validation-provider>
                  </template>
                </b-form-group>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="d-flex justify-content-between edit-actions mt-3">
              <b-button
                variant="outline-warning"
                size="sm"
                @click="handleTranslateSingleKeyWithAI(key)"
                :disabled="!getPolishTranslation(key, getTranslationsForKey) || isSingleTranslating">
                <b-spinner v-if="isSingleTranslating" small class="mr-1"></b-spinner>
                <feather-icon v-else icon="ZapIcon" size="14" class="mr-1" />
                {{ isSingleTranslating ? 'Tłumaczenie...' : 'Tłumacz z AI' }}
              </b-button>
              <div>
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  @click="saveAllTranslations"
                  :disabled="!areValuesValid">
                  <feather-icon icon="CheckIcon" size="14" class="mr-1" />
                  Zapisz
                </b-button>
                <b-button variant="outline-primary" size="sm" @click="cancelEdit">
                  <feather-icon icon="XIcon" size="14" class="mr-1" />
                  Anuluj
                </b-button>
              </div>
            </div>
          </div>
        </template>

        <!-- Read-only view -->
        <template v-else>
          <div class="multi-language-preview">
            <div class="row">
              <div v-for="language in availableLanguages" :key="language.lang" class="col-md-6 mb-1">
                <div class="language-preview">
                  <strong class="language-label">{{ language.value }}:</strong>
                  <div class="translation-preview-text">
                    <template v-if="!isLocaleAvailable(language.lang)">
                      <span class="text-muted font-italic">
                        <feather-icon icon="AlertCircleIcon" size="12" class="mr-1"></feather-icon>
                        Lokalizacja niedostępna
                      </span>
                    </template>
                    <template v-else-if="isLocaleLoading(language.lang)">
                      <b-spinner small class="mr-1"></b-spinner>
                      <span class="text-muted">Ładowanie...</span>
                    </template>
                    <template v-else>
                      <span
                        v-if="getTranslationForLanguage(key, language.lang)"
                        v-html="formatPreview(getTranslationForLanguage(key, language.lang))"></span>
                      <span v-else class="text-muted font-italic">Brak tłumaczenia</span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="Object.keys(filteredTranslations).length === 0" class="text-center py-4">
      <feather-icon icon="SearchIcon" size="48" class="text-muted mb-2" />
      <p class="text-muted">
        {{ searchTerm ? 'Nie znaleziono pasujących tłumaczeń' : 'Brak tłumaczeń w tej kategorii' }}
      </p>
    </div>

    <!-- Translation Preview Modal -->
    <translation-preview-modal
      :show="showTranslationPreview"
      :proposed-translations="proposedTranslations"
      :available-languages="availableLanguages"
      :get-translations-for-key="getTranslationsForKey"
      :is-single-key="isSingleKeyTranslation"
      @accept="onAcceptTranslations"
      @apply-to-fields="onApplyToFields"
      @cancel="onCancelTranslations" />
  </b-card>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { validateTranslationValue, searchTranslations } from '@/utils/translations';
import { useAITranslation } from '@/composables/useAITranslation';
import TranslationPreviewModal from './TranslationPreviewModal.vue';

export default {
  name: 'TranslationEditor',
  components: {
    ValidationProvider,
    ValidationObserver,
    TranslationPreviewModal,
  },
  props: {
    translations: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: 'GlobeIcon',
    },
    availableLocales: {
      type: Array,
      default: () => [],
    },
    availableLanguages: {
      type: Array,
      default: () => [],
    },
    getTranslationsForKey: {
      type: Function,
      required: true,
    },
    getLanguageCode: {
      type: Function,
      required: true,
    },
    loadingLocales: {
      type: Array,
      default: () => [],
    },
    selectedTranslationKey: {
      type: String,
      default: null,
    },
  },
  setup() {
    const aiTranslation = useAITranslation();
    return {
      ...aiTranslation,
    };
  },
  data() {
    return {
      searchTerm: '',
      filteredTranslations: {},
      editingKey: null,
      editingValues: {}, // { 'pl-PL': 'value', 'en-US': 'value', ... }
      isSingleKeyTranslation: false, // Track if current AI translation is for single key
      translateMissingOnly: false, // Toggle for AI translation mode (missing only vs all)
      showMissingOnly: false, // Toggle for list filtering (show only keys with any missing)
    };
  },
  computed: {
    areValuesValid() {
      return Object.values(this.editingValues).every((value) => {
        if (!value) return true; // Empty values are allowed
        const validation = validateTranslationValue(value);
        return validation.isValid;
      });
    },

    missingTranslationsCount() {
      if (!this.translations || !this.availableLanguages || !this.getTranslationsForKey) return 0;

      const targetLanguages = this.availableLanguages.filter((lang) => lang.lang !== 'pl').map((lang) => lang.locale);
      if (targetLanguages.length === 0) return 0;

      let missingCount = 0;

      Object.keys(this.translations).forEach((key) => {
        try {
          const allTranslations = this.getTranslationsForKey(key);
          const polishText = allTranslations['pl-PL'];

          if (polishText && polishText.trim()) {
            const hasMissingTranslations = targetLanguages.some((locale) => {
              const translation = allTranslations[locale];
              return !translation || !translation.trim();
            });

            if (hasMissingTranslations) {
              missingCount++;
            }
          }
        } catch (error) {
          console.warn('Error checking missing translations for key:', key, error);
        }
      });

      return missingCount;
    },

    translationsBadgeText() {
      if (this.translateMissingOnly) {
        return `${this.missingTranslationsCount} z brakującymi`;
      }
      return `${Object.keys(this.translations).length} tłumaczeń`;
    },
  },
  watch: {
    translations: {
      immediate: true,
      handler(newTranslations, oldTranslations) {
        this.applyFilters();
      },
    },
    searchTerm: {
      handler(newSearchTerm) {
        this.applyFilters();
      },
    },
    showMissingOnly: {
      handler() {
        this.applyFilters();
      },
    },
    selectedTranslationKey: {
      immediate: true,
      handler(newKey) {
        if (newKey) {
          // Set the search term to the selected key to filter the results
          this.searchTerm = newKey;
          // Automatically start editing the selected translation
          this.$nextTick(() => {
            if (this.filteredTranslations[newKey]) {
              this.editTranslation(newKey);
            }
          });
        }
      },
    },
  },
  methods: {
    handleSearch(searchTerm) {
      // Deprecated pathway; keep for compatibility
      this.applyFilters();
    },

    clearSearch() {
      this.searchTerm = '';
      // Clear the selected translation key when clearing search
      if (this.selectedTranslationKey) {
        this.$emit('clear-selected-key');
      }
    },

    applyFilters() {
      // Start with all translations
      let base = { ...this.translations };

      // If showMissingOnly, keep only keys where at least one non-PL locale is missing/empty
      if (this.showMissingOnly && this.getTranslationsForKey && this.availableLanguages) {
        const filtered = {};
        const nonPolish = this.availableLanguages.filter((l) => l.lang !== 'pl');
        Object.keys(base).forEach((key) => {
          const allTranslations = this.getTranslationsForKey(key);
          const polishText = allTranslations['pl-PL'];
          // consider only keys with Polish source text
          if (polishText && String(polishText).trim()) {
            const anyMissing = nonPolish.some((l) => {
              const val = allTranslations[l.locale];
              return !val || !String(val).trim();
            });
            if (anyMissing) {
              filtered[key] = base[key];
            }
          }
        });
        base = filtered;
      }

      // Apply search term on top
      this.filteredTranslations = searchTranslations(base, this.searchTerm);

      // If user manually changes search and it no longer matches the selected key, clear the selection
      if (this.selectedTranslationKey && this.searchTerm !== this.selectedTranslationKey) {
        this.$emit('clear-selected-key');
      }
    },

    editTranslation(key) {
      this.editingKey = key;
      // Load current values for all locales
      this.editingValues = this.getTranslationsForKey(key);
    },

    cancelEdit() {
      this.editingKey = null;
      this.editingValues = {};
      // Clear the selected translation key when cancelling edit
      if (this.selectedTranslationKey) {
        this.$emit('clear-selected-key');
      }
    },

    async saveAllTranslations() {
      if (!this.areValuesValid) return;

      try {
        // Include all values, including empty strings (but exclude null/undefined)
        const valuesToUpdate = {};
        Object.entries(this.editingValues).forEach(([locale, value]) => {
          if (value !== null && value !== undefined) {
            // Always include the value, even if it's an empty string after trimming
            valuesToUpdate[locale] = typeof value === 'string' ? value.trim() : String(value);
          }
        });

        this.$emit('update-multi-locale-translation', this.editingKey, valuesToUpdate);
        this.cancelEdit();
        // Clear the selected translation key after successful save
        if (this.selectedTranslationKey) {
          this.$emit('clear-selected-key');
        }
      } catch (error) {
        console.error('Error saving translations:', error);
      }
    },

    getTranslationForLanguage(key, languageCode) {
      const localeCode = this.getLocaleCode(languageCode);
      const allTranslations = this.getTranslationsForKey(key);
      return allTranslations[localeCode] || '';
    },

    getLocaleCode(languageCode) {
      // Find the full locale code for a language (e.g., 'pl' -> 'pl-PL')
      const lang = this.availableLanguages.find((l) => l.lang === languageCode);
      return lang ? lang.locale : `${languageCode}-${languageCode.toUpperCase()}`;
    },

    isLocaleLoading(languageCode) {
      const localeCode = this.getLocaleCode(languageCode);
      return this.loadingLocales.includes(localeCode);
    },

    isLocaleAvailable(languageCode) {
      const localeCode = this.getLocaleCode(languageCode);
      return this.availableLanguages.some((l) => l.locale === localeCode);
    },

    formatPreview(value) {
      if (typeof value !== 'string') return value;

      // Truncate long values
      if (value.length > 80) {
        return value.substring(0, 80) + '...';
      }

      // Highlight HTML tags
      return value.replace(/<[^>]+>/g, '<span class="text-warning">$&</span>');
    },

    getTextareaRows(value) {
      if (!value) return 3;
      const lines = value.split('\n').length;
      return Math.max(3, Math.min(8, lines + 1));
    },

    getValidationRules() {
      return 'max:5000';
    },

    async handleTranslateAllWithAI() {
      console.log('Starting AI translation for all...', { translateMissingOnly: this.translateMissingOnly });

      // Set flag to indicate this is a bulk translation
      this.isSingleKeyTranslation = false;

      const success = await this.translateAllWithAI(
        this.translations,
        this.getTranslationsForKey,
        this.availableLanguages,
        this.translateMissingOnly,
      );

      console.log('AI translation completed:', {
        success,
        showModal: this.showTranslationPreview,
        proposedCount: Object.keys(this.proposedTranslations).length,
      });

      if (!success && this.lastError) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Błąd tłumaczenia',
            text: this.lastError,
            variant: 'warning',
          },
        });
      }
    },

    async handleTranslateSingleKeyWithAI(key) {
      console.log('Starting AI translation for key:', key);

      // Set flag to indicate this is a single key translation
      this.isSingleKeyTranslation = true;

      // Ensure the key is being edited so we can apply translations to fields
      if (this.editingKey !== key) {
        this.editTranslation(key);
      }

      const success = await this.translateSingleKeyWithAI(key, this.getTranslationsForKey, this.availableLanguages);

      console.log('AI translation completed:', {
        success,
        showModal: this.showTranslationPreview,
        proposedCount: Object.keys(this.proposedTranslations).length,
      });

      if (!success && this.lastError) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Błąd tłumaczenia',
            text: this.lastError,
            variant: 'warning',
          },
        });
      }
    },

    async onAcceptTranslations(acceptedTranslations) {
      console.log('Accepting AI translations:', acceptedTranslations);
      const success = await this.acceptTranslations(acceptedTranslations);

      if (success) {
        console.log('AI translations accepted successfully');

        // For bulk translations, update the state efficiently without full refresh
        if (!this.isSingleKeyTranslation) {
          // Use the new efficient update method from the parent
          this.$emit('update-translations-in-state', acceptedTranslations);
        }
        // For single-key translations, no state update needed since they're applied to fields

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Tłumaczenia zaktualizowane',
            text: `Zaktualizowano ${Object.keys(acceptedTranslations).length} tłumaczeń.`,
            variant: 'success',
          },
        });
      } else {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Błąd aktualizacji',
            text: this.lastError || 'Wystąpił błąd podczas aktualizacji tłumaczeń.',
            variant: 'danger',
          },
        });
      }
    },

    onCancelTranslations() {
      this.cancelTranslations();
    },

    onApplyToFields(acceptedTranslations) {
      console.log('Applying AI translations to fields:', acceptedTranslations);
      const success = this.applyTranslationsToFields(acceptedTranslations);

      if (success) {
        console.log('AI translations applied successfully');
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Tłumaczenia zastosowane',
            text: 'Tłumaczenia AI zostały zastosowane w polach edycji. Kliknij "Zapisz" aby je zatwierdzić.',
            variant: 'success',
          },
        });
      } else {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Błąd aktualizacji',
            text: 'Wystąpił błąd podczas aktualizacji tłumaczeń.',
            variant: 'danger',
          },
        });
      }
    },

    applyTranslationsToFields(acceptedTranslations) {
      try {
        // Apply the accepted translations to the editing fields
        Object.entries(acceptedTranslations).forEach(([key, localeValues]) => {
          if (key === this.editingKey) {
            // Update the editing values with the accepted translations
            Object.entries(localeValues).forEach(([locale, value]) => {
              this.$set(this.editingValues, locale, value);
            });
          }
        });

        // Close the modal
        this.cancelTranslations();

        return true;
      } catch (error) {
        console.error('Error applying translations to fields:', error);
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.translation-editor {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.translation-item {
  background-color: #f8f9fa;
  transition: all 0.3s ease;
}

.translation-item:hover {
  background-color: #e9ecef;
}

.translation-key {
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.9em;
  word-break: break-all;
}

.multi-language-editor {
  background-color: #ffffff;
  border: 2px solid black;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.5rem;
}

.multi-language-preview {
  margin-top: 0.5rem;
}

.language-preview {
  padding: 0.5rem;
  background-color: #ffffff;
  border-radius: 4px;
  border-left: 3px solid #e9ecef;
}

.language-label {
  font-size: 0.85em;
  color: #6c757d;
  display: block;
  margin-bottom: 0.25rem;
}

.translation-preview-text {
  color: #495057;
  font-size: 0.9em;
  line-height: 1.4;
  min-height: 1.4em;
}

.translations-list {
  max-height: 800px;
  overflow-y: auto;
}

.form-group label {
  font-weight: 600;
  font-size: 0.9em;
}

.ai-mode-checkbox {
  label {
    display: none;
  }
}

.edit-actions {
  margin-top: 1rem;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-left: 0;
    margin-top: 1rem;
    gap: 1.5rem;
    width: 100%;
  }
}

// Responsive styling for AI translation toggle
@media (max-width: 768px) {
  .d-flex.align-items-center.ml-1 {
    flex-direction: column;
    align-items: flex-start !important;
    margin-left: 0 !important;
    margin-top: 1rem;
  }

  .d-flex.align-items-center.mr-3 {
    margin-right: 0 !important;
    margin-bottom: 0.5rem;
  }
}
</style>
